import { closeCurrentTag } from '@/utils/tags';
import { handleDatabaseRequestError } from '@/utils/error';

export default {
  props: {
    autoGeneratedId: {
      default: true,
      type: Boolean
    },
    definition: {
      default() {
        return {};
      },
      type: Object
    },
    disabled: {
      default: false,
      type: Boolean
    },
    id: {
      type: Number,
      default: 0
    },
    fetchById: {
      default: id => {
        throw new Error('fetchById is not provided as a prop');
      },
      type: Function
    },
    isEdit: {
      default: false,
      type: Boolean
    },
    itemType: {
      default: '',
      type: String
    },
    returnTo: {
      default: '',
      type: String
    },
    save: {
      default: data => {
        throw new Error('save is not provided as a prop');
      },
      type: Function
    },
    pageLoading: {
      type: Boolean,
      default: false
    },
    isEmbedded: {
      type: Boolean,
      default: false
    },
    forceValues: {
      type: Object,
      default: () => ({})
    },
    saveButtonPosition: {
      type: String,
      default: 'top'
    },
    preventDefault: {
      type: Boolean,
      default: false
    },
    getDependencies: {
      type: Function,
      default: () => []
    },
    promptRouteLeave: Function
  },
  data() {
    const fieldsDefinition = this.definition;
    const fieldNames = Object.keys(fieldsDefinition);
    this.defaultForm = Object.assign(
      {},
      fieldNames.reduce((form, name) => {
        form[name] = fieldsDefinition[name].defaultValue;
        return form;
      }, {})
    );

    const fields = {};
    fieldNames.reduce((target, name) => {
      const field = fieldsDefinition[name];
      if (field.type) {
        target[name] = field;
      }
      return target;
    }, fields);

    return {
      errorMessage: '',
      formItemStyle: {},
      errors: {},
      formData: {},
      form: Object.assign({}, this.defaultForm),
      actionLoading: false,
      tempRoute: {},
      userListOptions: [],
      formLabelPosition: 'right',
      forceExit: false
    };
  },
  computed: {
    fields() {
      const fieldsDefinition = this.definition;
      const fieldNames = Object.keys(fieldsDefinition);

      const fields = {};
      fieldNames.reduce((target, name) => {
        const field = fieldsDefinition[name];
        if (field.type) {
          target[name] = field;
        }
        return target;
      }, fields);

      return fields;
    },
    columnsFields() {
      return this.splitFieldsInHalfAsArray(this.fields);
    },
    lang() {
      return this.$store.getters.language;
    },
    formHasChanged() {
      return this.formObjectsAreDifferent(this.form, this.formData);
    },
    validationErrorInputPrefix() {
      return this.$store.getters['validationErrorInputPrefix'];
    }
  },
  watch: {
    forceValues(val) {
      this.refreshFormValues();
    },
    definition(definition) {
      this.refreshFormValues();
    },
    id(val) {
      if (this.isEdit) {
        const id = this.id || (this.$route.params && this.$route.params.id);
        this.fetchData(id);
      }
    },
    form: {
      deep: true,
      handler: function(val) {
        this.$store.dispatch('editor/updateForm', val);
      }
    }
  },
  created() {
    window.addEventListener('beforeunload', this.beforeUnload, false);
    if (this.isEdit) {
      const id = this.id || (this.$route.params && this.$route.params.id);
      this.fetchData(id).then(data => {
        const dependencies = this.getDependencies(data);
        dependencies.forEach(dep => {
          const params = dep.params || {};
          if (dep.trashedID) {
            params.query = {
              withTrashedID: dep.trashedID
            };
          }
          this.$store.dispatch(dep.entity + '/getItems', params);
        });
      });
    } else {
      this.fetchDependencies();
    }
    this.refreshFormValues();
    // Why need to make a copy of this.$route here?
    // Because if you enter this page and quickly switch tag, may be in the execution of the setTagsViewTitle function,
    // this.$route is no longer pointing to the current page
    // https://github.com/PanJiaChen/vue-element-admin/issues/1221
    this.tempRoute = Object.assign({}, this.$route);
    this.$store.dispatch('notify/disableErrorCodePrefix', this.validationErrorInputPrefix);
  },
  destroyed() {
    window.removeEventListener('beforeunload', this.beforeUnload);
    window.removeEventListener('resize', this.handleResize);
    this.$store.dispatch('notify/enableErrorCodePrefix', this.validationErrorInputPrefix);
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  methods: {
    doPromptRouteLeave() {
      if (this.promptRouteLeave) {
        return this.promptRouteLeave();
      }
      return this.$confirm(this.$i18n.t('common.leave_unsaved_warning'), this.$i18n.t('common.warning'), {
        distinguishCancelAndClose: true,
        confirmButtonText: this.$i18n.t('common.save'),
        cancelButtonText: this.$i18n.t('common.dontSave'),
        type: 'warning'
      });
    },
    handleRouteLeave(to, from, next) {
      if (this.formHasChanged && !this.forceExit) {
        const saveErrorMsg = this.$i18n.t('error.save');
        this.doPromptRouteLeave()
          .then(async () => {
            try {
              const done = await this.persistForm();
              if (done) {
                next();
              }
            } catch (err) {
              this.$store.dispatch('toast', { message: saveErrorMsg });
              console.log(err);
            }
          })
          .catch(action => {
            if (action === 'cancel') {
              next();
            } else {
              next(false);
            }
          });
      } else {
        next();
      }
    },
    beforeUnload(e) {
      if (this.formHasChanged) {
        // https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    },
    fetchDependencies(dep) {
      const dependencies = this.getDependencies();
      dependencies.forEach(dep => {
        const params = dep.params || {};
        this.$store.dispatch(dep.entity + '/getItems', params);
      });
    },
    handleResize() {
      const { width } = this.$el.getBoundingClientRect();
      if (width <= 767) {
        this.formLabelPosition = 'top';
      } else {
        this.formLabelPosition = 'right';
      }
    },
    splitFieldsInHalfAsArray(fields) {
      const keys = Object.keys(fields);
      if (keys.length < 4) {
        const columns = [];
        columns.push(fields);
        return columns;
      }
      let columns = [];
      columns = keys.reduce((acc, key, index) => {
        const columnIndex = index < keys.length / 2 ? 0 : 1;
        if (!acc[columnIndex]) {
          acc[columnIndex] = {};
        }
        acc[columnIndex][key] = fields[key];
        return acc;
      }, columns);
      return columns;
    },
    handleInit(name, init) {
      this.formData[name] = init;
    },
    clearForm() {
      this.formData = {};
      this.refreshFormValues();
      this.$refs.form.clearValidate();
    },
    refreshFormValues() {
      // this.form cannot be Computed because it is passed as v-model for input components
      const fieldsDefinition = this.definition;
      const fieldNames = Object.keys(fieldsDefinition);
      const defaultForm = Object.assign(
        {},
        fieldNames.reduce((form, name) => {
          form[name] = fieldsDefinition[name].defaultValue;
          return form;
        }, {})
      );
      this.form = Object.assign({}, defaultForm, this.formData, this.forceValues);
      this.formData = Object.assign({}, this.form, this.formData, this.forceValues);
    },
    fetchData(id) {
      return this.fetchById(id)
        .then(data => {
          this.formData = Object.assign(this.formData, data);
          this.$set(this.formData, 'id', data.id);
          this.refreshFormValues();
          this.setTagsViewTitle(data.name || '');
          this.$refs.form.clearValidate();
          return data;
        })
        .catch(error => {
          console.log(error);
        });
    },
    setTagsViewTitle(name) {
      if (this.isEmbedded) {
        return;
      }
      const title = this.$i18n.t('route.edit' + this.itemType) + ' ' + (name || '');
      const route = Object.assign({}, this.tempRoute, {
        title: `${title}`
      });
      this.$store.dispatch('updateVisitedView', route);
    },
    async persistForm() {
      this.errorMessage = '';
      this.form = Object.assign(this.form, this.forceValues);
      try {
        await this.$refs.form.validate();
      } catch (err) {
        this.$store.dispatch('notify/formValidationFailed');
        return false;
      }

      if (!this.isEdit && this.autoGeneratedId) {
        // make sure no id is present
        delete this.form.id;
      }
      this.actionLoading = true;
      this.$emit('save');
      this.errors = Object.assign({});
      try {
        const response = await this.save(this.form);
        let message = response;
        if (!message || !(typeof message === 'string')) {
          if (this.isEdit) {
            message = this.$i18n.t('common.data_updated');
          } else {
            message = this.$i18n.t('common.data_created');
          }
        }
        if (!this.preventDefault) {
          this.$store.dispatch('notify/success', { message });
        }
        this.actionLoading = false;
        this.formData = Object.assign(this.formData, this.form, {});
        this.$emit('saved', this.form);
        return response || true;
      } catch (error) {
        this.errors = handleDatabaseRequestError(this, error, this.preventDefault);
        this.actionLoading = false;
        return false;
      }
    },
    async handleSave() {
      const item = await this.persistForm();
      if (item && !this.isEdit && !this.isEmbedded && !this.preventDefault) {
        closeCurrentTag(
          this,
          {
            name: 'Edit' + this.itemType,
            params: { id: item.id }
          },
          false
        );
      }
      return item;
    },
    async saveAndClose() {
      const done = await this.persistForm();
      if (done && !this.preventDefault) {
        closeCurrentTag(this, false, { name: this.returnTo });
      }
    },
    formArraysAreDifferent(arr1, arr2) {
      if (arr1.length !== arr2.length) {
        return true;
      }
      const _arr1 = arr1.sort();
      const _arr2 = arr2.sort();
      const isDifferent = _arr1.some((val, index) => {
        if (_arr1[index] && typeof _arr2[index] === typeof {}) {
          return this.formObjectsAreDifferent(_arr1[index], _arr2[index]);
        }
        return _arr1[index] !== _arr2[index];
      });
      return isDifferent;
    },
    formObjectsAreDifferent(obj1, obj2) {
      const isDifferent = Object.keys(obj1).some(key => {
        if (obj1[key] && typeof obj1[key] === typeof {}) {
          if (Array.isArray(obj1[key])) {
            return this.formArraysAreDifferent(obj1[key], obj2[key]);
          } else {
            return this.formObjectsAreDifferent(obj1[key], obj2[key]);
          }
        }
        // Assume null to be same as empty string
        const _val1 = obj1[key] === null ? '' : obj1[key];
        const _val2 = obj2 === null || obj2[key] === null ? '' : obj2[key];
        // Debug code
        // if (_val1 !== _val2) {
        //   console.log('different: ', { key, val1: obj1[key], val2: obj2[key] });
        // }
        return _val1 !== _val2;
      });
      return isDifferent;
    },
    async close() {
      try {
        if (this.formHasChanged) {
          await this.$store.dispatch('notify/closeUnsavedConfirm');
        }
        if (!this.preventDefault) {
          this.forceExit = true;
          closeCurrentTag(this, false, { name: this.returnTo });
        }
      } catch (err) {
        // do nothing and stay on page
      }
    }
  }
};
